import { ChangeDetectionStrategy, Component, Injector, Type, input, signal, computed, inject, OnDestroy, effect, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormArray, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';

// PrimeNG
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService, SharedModule } from 'primeng/api';

// Shared
import { DynamicTableComponent } from '../../components/dynamic-table/dynamic-table.component';
import { TableColumn, RowAction, ActionEvent } from '../../components/dynamic-table/dynamic-table.interface';
import { FormFieldComponent } from '../form-field/form-field.component';
import { DynamicFormService } from '../../services/dynamic-form.service';
import { FilterConfig } from '../../models/page-config.model';
import { FormOptionsService } from '../../../core/services/form-options.service';
import { evaluateCondition } from '../../../core/services/form-config-adapter.service';
import { FieldRegistryService } from '../../services/field-registry.service';
import { UnknownFieldComponent } from '../form-fields/unknown-field.component';
import { FIELD_CONTROL, FIELD_CONFIG } from '../form-fields/field-tokens';

@Component({
  selector: 'app-dynamic-table-editor',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule, ReactiveFormsModule,
    DialogModule, ButtonModule, TooltipModule, SharedModule,
    DynamicTableComponent, FormFieldComponent
  ],
  templateUrl: './dynamic-table-editor.component.html'
})
export class DynamicTableEditorComponent implements OnDestroy {
  // Same field-component-registry pattern as DynamicFormRendererComponent (top-level forms) —
  // any type registered there (app.config.ts) now renders correctly here too, with no
  // per-type template/adapter code needed in this component going forward.
  private readonly injector = inject(Injector);
  private readonly fieldRegistry = inject(FieldRegistryService);
  private readonly fieldInjectorCache = new Map<string, Injector>();

  formArray = input.required<FormArray>();
  factory = input<() => any>();
  itemValidators = input<{ [key: string]: any }>({});
  arrayConfig = input<any>();

  private readonly confirmationService = inject(ConfirmationService);
  private readonly dynamicFormService = inject(DynamicFormService);
  private readonly optionsService = inject(FormOptionsService);
  private sub: Subscription | null = null;
  private dialogSubs: Subscription[] = [];

  // Dialog State
  isDialogOpen = signal(false);
  isNewItem = signal(false);
  isViewOnlyDialog = signal(false);
  editingIndex = signal<number>(-1);
  activeFormGroup = signal<FormGroup | null>(null);

  // Table Data & Filters
  private readonly _tableData = signal<any[]>([]);
  tableData = this._tableData.asReadonly();
  searchQuery = signal('');
  selectedStatus = signal<any>(null);

  filteredTableData = computed(() => {
    const query = this.searchQuery().toLowerCase();
    const statusVal = this.selectedStatus()?.value || this.selectedStatus();

    return this.tableData().filter(item => {
      // Generic search across common fields or fallback to item.name
      const matchesSearch = !query ||
        ['name', 'description', 'title', 'label'].some(key =>
          item[key]?.toLowerCase().includes(query)
        );
      const matchesStatus = !statusVal || item.displayStatus === statusVal;
      return matchesSearch && matchesStatus;
    });
  });

  filterConfigs = computed<FilterConfig[]>(() => {
    const base = this.arrayConfig()?.tableFilters || [];
    return base.map((f: any) => ({
      ...f,
      value: f.id === 'search' ? () => this.searchQuery() : () => this.selectedStatus(),
      onChange: f.id === 'search' ? (val: any) => this.searchQuery.set(val ?? '') : (val: any) => this.selectedStatus.set(val)
    }));
  });

  showPaginator = computed(() => this.arrayConfig()?.paginator !== false);
  showTableToolbar = computed(() => this.arrayConfig()?.showToolbar !== false);

  emptyMessage = computed(() => {
    const name = this.arrayConfig()?.entityName || 'item';
    return `No ${name}s added yet.`;
  });
  emptySubMessage = computed(() => {
    const name = this.arrayConfig()?.entityName || 'Item';
    return `Click 'Add ${name}' above to add one.`;
  });

  columns = computed<TableColumn[]>(() => this.arrayConfig()?.tableColumns || []);

  readonly allActions: RowAction[] = [
    { id: 'view', icon: 'pi pi-eye', tooltip: 'View', severity: 'info', position: 'inline' },
    { id: 'edit', icon: 'pi pi-pencil', tooltip: 'Edit', severity: 'warn', position: 'menu' },
    { id: 'delete', icon: 'pi pi-trash', tooltip: 'Delete', severity: 'danger', position: 'menu' }
  ];

  visibleActions = computed<RowAction[]>(() => {
    const showView = this.arrayConfig()?.showViewAction ?? true;
    return showView ? this.allActions : this.allActions.filter(a => a.id !== 'view');
  });

  getColSpanClass(colSpan: number | undefined): string {
    if (!colSpan) return 'col-span-12';
    if (colSpan === 3) return 'col-span-12';
    if (colSpan === 2) return 'col-span-12 md:col-span-8';
    if (colSpan === 1) return 'col-span-12 md:col-span-4';
    return 'col-span-12';
  }

  isFieldVisible(field: any, group: FormGroup): boolean {
    if (!field.condition) return true;
    const ctrl = group.get(field.condition.field);
    return evaluateCondition(field.condition, ctrl ? ctrl.value : null);
  }

  /** Resolves a field's registered component (same registry the top-level dynamic form
   * uses) — falls back to a visible "unsupported type" placeholder instead of rendering
   * nothing, so a schema typo/gap is obvious rather than silently blank. */
  resolveFieldComponent(type: string): Type<any> {
    return this.fieldRegistry.resolve(type) ?? UnknownFieldComponent;
  }

  /** Builds (and caches, per field key, for the lifetime of the current dialog) the DI
   * injector that supplies FIELD_CONTROL/FIELD_CONFIG to the dynamically-outlet-ed field
   * component — identical contract to DynamicFormRendererComponent.buildFieldInjector. */
  buildFieldInjector(field: any, group: FormGroup): Injector {
    const cached = this.fieldInjectorCache.get(field.key);
    if (cached) return cached;

    const control = group.get(field.key);
    const inj = Injector.create({
      providers: [
        { provide: FIELD_CONTROL, useValue: control },
        { provide: FIELD_CONFIG, useValue: field }
      ],
      parent: this.injector
    });
    this.fieldInjectorCache.set(field.key, inj);
    return inj;
  }

  constructor() {
    effect(() => {
      const currentArray = this.formArray();
      if (this.sub) this.sub.unsubscribe();
      untracked(() => this.refreshTableData());
      this.sub = currentArray.valueChanges.subscribe(() => this.refreshTableData());
    });

    effect(() => {
      const group = this.activeFormGroup();
      // Clean up previous dialog subscriptions
      this.dialogSubs.forEach(s => s.unsubscribe());
      this.dialogSubs = [];
      // Injectors are scoped to one dialog's FormGroup instance — stale ones would hand a
      // freshly-outlet-ed field component a control from the PREVIOUS dialog.
      this.fieldInjectorCache.clear();

      if (!group) return;

      const fields = this.arrayConfig()?.dialogFields || [];
      const isViewOnly = this.isViewOnlyDialog();

      // Normalize `options` to always be a callable accessor — field components (registry)
      // call `config.options()` unconditionally, but some hand-authored dialogFields configs
      // (and any schema not yet wrapping static/api options) declare it as a plain array.
      for (const field of fields) {
        if (field.options && typeof field.options !== 'function') {
          const snapshot = field.options;
          field.options = () => snapshot;
        }
      }

      for (const field of fields) {
        if (field.dependsOn) {
          const parentCtrl = group.get(field.dependsOn);
          const childCtrl = group.get(field.key);
          if (!parentCtrl || !childCtrl) continue;

          // Replace child options accessor with a filtered one
          field.options = this.optionsService.getFilteredOptionsAccessor(
            field.dependsApiKey,
            field.__depValueField ?? 'id',
            field.dependsParam,
            parentCtrl,
            field.__depLabelField,
            field.__depCompositeFields,
            field.__depSeparator
          );

          // Disable initially if parent has no value
          const updateDisableStatus = (parentVal: any) => {
            if (!isViewOnly && parentVal != null && parentVal !== '') {
              childCtrl.enable({ emitEvent: false });
            } else {
              childCtrl.disable({ emitEvent: false });
            }
          };
          updateDisableStatus(parentCtrl.value);

          // Initial fetch if parent has a value
          if (parentCtrl.value && field.__depEndpoint) {
            this.optionsService.loadDependentOptions(
              field.dependsApiKey,
              field.__depEndpoint,
              field.dependsParam,
              parentCtrl.value
            ).catch(err => console.error('[DynamicTableEditor] Failed to load initial dependent options:', err));
          }

          // Subscribe to parent value changes
          const sub = parentCtrl.valueChanges.subscribe(newVal => {
            childCtrl.setValue(null, { emitEvent: false });
            updateDisableStatus(newVal);
            if (newVal && field.__depEndpoint) {
              this.optionsService.loadDependentOptions(
                field.dependsApiKey,
                field.__depEndpoint,
                field.dependsParam,
                newVal
              ).catch(err => console.error('[DynamicTableEditor] Failed to load dependent options:', err));
            }
          });
          this.dialogSubs.push(sub);
        }
      }

      // Generic dependentDisable: clear + disable a field while the field it depends on is truthy
      // (e.g. lock "period" while the "use_custom_end_date" toggle is on)
      for (const field of fields) {
        if (!field.dependentDisable) continue;
        const ctrl = group.get(field.key);
        const depCtrl = group.get(field.dependentDisable);
        if (!ctrl || !depCtrl) continue;

        const applyDisableState = (depVal: any) => {
          if (depVal) {
            ctrl.setValue(null, { emitEvent: true });
            ctrl.disable({ emitEvent: true });
          } else if (!isViewOnly) {
            ctrl.enable({ emitEvent: true });
          }
        };

        applyDisableState(depCtrl.value);
        const sub = depCtrl.valueChanges.subscribe(applyDisableState);
        this.dialogSubs.push(sub);
      }

      // Generic dependentEnable: a field is only editable while the field it depends on is truthy
      // (e.g. "to_date" stays locked unless the "use_custom_end_date" toggle is on)
      for (const field of fields) {
        if (!field.dependentEnable) continue;
        const ctrl = group.get(field.key);
        const depCtrl = group.get(field.dependentEnable);
        if (!ctrl || !depCtrl) continue;

        const applyEnableState = (depVal: any) => {
          if (depVal && !isViewOnly) {
            ctrl.enable({ emitEvent: true });
          } else {
            ctrl.disable({ emitEvent: true });
          }
        };

        applyEnableState(depCtrl.value);
        const sub = depCtrl.valueChanges.subscribe(applyEnableState);
        this.dialogSubs.push(sub);
      }

      // Generic condition: clear a field's value once it becomes hidden so stale data
      // doesn't linger (e.g. bond_value/current_bond_value when commitment_bond != YES)
      for (const field of fields) {
        if (!field.condition) continue;
        const ctrl = group.get(field.key);
        const condCtrl = group.get(field.condition.field);
        if (!ctrl || !condCtrl) continue;

        const applyCondition = (val: any) => {
          if (!evaluateCondition(field.condition, val)) {
            ctrl.setValue(null, { emitEvent: false });
          }
        };

        applyCondition(condCtrl.value);
        const sub = condCtrl.valueChanges.subscribe(applyCondition);
        this.dialogSubs.push(sub);
      }

      // Autofill wiring (e.g. from_date + period -> to_date), de-duplicated by target field
      const autofillTargets = new Map<string, { sourceKeys: string[]; rule: string }>();
      for (const field of fields) {
        if (field.autofill && !autofillTargets.has(field.autofill.target_field)) {
          autofillTargets.set(field.autofill.target_field, {
            sourceKeys: field.autofill.source_fields,
            rule: field.autofill.rule
          });
        }
      }

      for (const [targetKey, { sourceKeys, rule }] of autofillTargets) {
        const targetCtrl = group.get(targetKey);
        const sourceCtrls = sourceKeys.map(key => group.get(key));
        if (!targetCtrl || sourceCtrls.some(c => !c)) continue;

        const recompute = () => {
          if (rule !== 'add_months') return;
          const [dateCtrl, monthsCtrl] = sourceCtrls;
          const baseValue = dateCtrl!.value;
          const baseDate = baseValue instanceof Date ? baseValue : (baseValue ? new Date(baseValue) : null);
          const monthsRaw = monthsCtrl!.value;
          const months = Number(monthsRaw);
          const hasValidPeriod = !monthsCtrl!.disabled && monthsRaw !== null && monthsRaw !== '' && Number.isInteger(months) && months > 0;

          if (hasValidPeriod && baseDate && !Number.isNaN(baseDate.getTime())) {
            const computed = new Date(baseDate);
            computed.setMonth(computed.getMonth() + months);
            targetCtrl.setValue(computed, { emitEvent: false });
          }
        };

        recompute();
        for (const ctrl of sourceCtrls) {
          const sub = ctrl!.valueChanges.subscribe(() => recompute());
          this.dialogSubs.push(sub);
        }
      }

      // Readonly / auto-computed fields are not directly editable
      for (const field of fields) {
        if (field.readonly) {
          const ctrl = group.get(field.key);
          if (ctrl) ctrl.disable({ emitEvent: false });
        }
      }

      // View-only dialog: every field renders through its real (now-disabled) component
      // instead of a bespoke read-only markup block — this is what lets a type work here
      // with zero extra code, matching how the top-level form treats view-without-edit.
      if (isViewOnly) {
        group.disable({ emitEvent: false });
      }
    });
  }

  ngOnDestroy() {
    if (this.sub) this.sub.unsubscribe();
    this.dialogSubs.forEach(s => s.unsubscribe());
  }

  refreshTableData() {
    const raw = this.formArray().getRawValue();
    this._tableData.set(
      raw.map((item, index) => ({
        ...item,
        displayStatus: this.computeDisplayStatus(item),
        _idx: index
      }))
    );
  }

  onTableAction(event: ActionEvent) {
    const index = event.data._idx;
    if (event.action.id === 'view') this.openViewDialog(index);
    else if (event.action.id === 'edit') this.openEditDialog(index);
    else if (event.action.id === 'delete') this.confirmDeletion(index);
  }

  onAddClick() {
    const factoryFn = this.factory();
    if (!factoryFn) return;
    const group = this.dynamicFormService.buildItemGroup(factoryFn(), this.itemValidators());
    this.isViewOnlyDialog.set(false);
    this.isNewItem.set(true);
    this.activeFormGroup.set(group);
    this.isDialogOpen.set(true);
  }

  openViewDialog(index: number) {
    const sourceGroup = this.formArray().at(index) as FormGroup;
    const group = this.dynamicFormService.buildItemGroup(sourceGroup.getRawValue(), this.itemValidators());

    this.editingIndex.set(index);
    this.isViewOnlyDialog.set(true);
    this.isNewItem.set(false);
    this.activeFormGroup.set(group);
    this.isDialogOpen.set(true);
  }

  openEditDialog(index: number) {
    const sourceGroup = this.formArray().at(index) as FormGroup;
    const group = this.dynamicFormService.buildItemGroup(sourceGroup.getRawValue(), this.itemValidators());
    this.editingIndex.set(index);
    this.isViewOnlyDialog.set(false);
    this.isNewItem.set(false);
    this.activeFormGroup.set(group);
    this.isDialogOpen.set(true);
  }

  saveItem() {
    const group = this.activeFormGroup();
    if (group?.invalid) {
      group.markAllAsTouched();
      return;
    }

    if (group) {
      const displayStatus = this.computeDisplayStatus(group.getRawValue());
      group.patchValue({ displayStatus }, { emitEvent: false });
    }

    if (this.isNewItem() && group) {
      this.formArray().push(group);
    } else if (!this.isViewOnlyDialog() && !this.isNewItem() && group) {
      const index = this.editingIndex();
      if (index > -1) {
        this.formArray().setControl(index, group);
      }
    }
    this.closeDialog();
  }

  closeDialog() {
    this.isDialogOpen.set(false);
    this.activeFormGroup.set(null);
    this.editingIndex.set(-1);
    this.isNewItem.set(false);
    this.isViewOnlyDialog.set(false);
  }

  private confirmDeletion(index: number) {
    const entityName = this.arrayConfig()?.entityName?.toLowerCase() || 'item';
    const itemName = this.formArray().at(index).get('name')?.value || `this ${entityName}`;

    this.confirmationService.confirm({
      header: 'Confirm Deletion',
      message: `Are you sure you want to remove "${itemName}"?`,
      icon: 'pi pi-exclamation-triangle text-danger',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      accept: () => {
        this.formArray().removeAt(index);
        this.refreshTableData();
      }
    });
  }

  private computeDisplayStatus(item: any): string {
    const status = item?.status || 'Draft';
    if (status !== 'Active') {
      return status;
    }

    if (item?.hasNoExpiry || !item?.expiryDate) {
      return status;
    }

    const expiry = this.parseDateOnly(item.expiryDate);
    if (!expiry) {
      return status;
    }

    const today = this.startOfTodayUtc();
    const daysUntilExpiry = Math.floor((expiry.getTime() - today.getTime()) / 86400000);

    if (daysUntilExpiry < 0) {
      return 'Expired';
    }

    if (daysUntilExpiry <= 30) {
      return 'Expiring Soon';
    }

    return status;
  }

  private parseDateOnly(value: unknown): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) {
        return null;
      }
      return new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));
    }

    if (typeof value !== 'string') {
      return null;
    }

    const [yearText, monthText, dayText] = value.split('-');
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);

    if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
      return null;
    }

    return new Date(Date.UTC(year, month - 1, day));
  }

  private startOfTodayUtc(): Date {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  }
}
